/**
 * LocationVenueService
 * Service for LocationVenue entity operations
 * Route: api/location-venues
 * Controller: LocationVenuesController.cs
 *
 * Prefer deactivating (`update(id, { isActive: false })`). DELETE archives (soft delete); `restore` undoes it.
 */
import { CrudService } from '../base/CrudService';
/**
 * LocationVenueService
 * Handles venue CRUD operations and custom endpoints
 * Maps to /api/location-venues endpoint
 */
export class LocationVenueService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationVenueService.DEFAULT_SERVICE_PATH,
            basePath: LocationVenueService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all (non-archived) venues for a specific location
     * GET /api/location-venues/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of venues
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all venues for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of venues
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Archived (soft-deleted) venues, newest archive first, for a restore list
     * GET /api/location-venues/archived?locationId=
     * @param locationId - Optional location (0 is the Online Campus, a real location); omit for every location
     */
    async getArchived(locationId) {
        const response = await this.getArchivedRaw(locationId);
        return response.data ?? [];
    }
    getArchivedRaw(locationId) {
        const endpoint = this.getEndpoint('archived', 'archived');
        return this.http.get(this.buildUrlWithParams(endpoint, { locationId }));
    }
    /**
     * Archive a venue (soft delete) — same call as `delete`, returns the archived row
     * DELETE /api/location-venues/{id}
     */
    async archive(id) {
        const response = await this.archiveRaw(id);
        if (!response.data) {
            throw new Error(`Failed to archive venue ${id}`);
        }
        return response.data;
    }
    archiveRaw(id) {
        const endpoint = this.getEndpoint('archive', `${id}`);
        return this.http.delete(this.buildUrl(endpoint));
    }
    /**
     * Restore an archived venue (it comes back inactive)
     * POST /api/location-venues/{id}/restore
     */
    async restore(id) {
        const response = await this.restoreRaw(id);
        if (!response.data) {
            throw new Error(`Failed to restore venue ${id}`);
        }
        return response.data;
    }
    restoreRaw(id) {
        const endpoint = this.getEndpoint('restore', `${id}/restore`);
        return this.http.post(this.buildUrl(endpoint), {});
    }
}
LocationVenueService.DEFAULT_SERVICE_PATH = 'locations';
LocationVenueService.BASE_PATH = 'api/location-venues';
//# sourceMappingURL=LocationVenueService.js.map